// Decides which asset class a page load starts in. The URL is the source of truth: two tabs on two
// asset classes must not influence each other, so the last choice kept in localStorage is only a
// fallback for URLs that carry no prefix.

import { routes } from '../router/routes'
import {
  KNOWN_ASSET_CLASSES,
  LEGACY_ASSET_CLASS,
  buildAssetClassUrl,
  slugFromPath,
  stripSlugFromPath,
} from './assetClassSlugs'

// First path segment of every "resource route" (a route declared `list`: it shows one resource that
// belongs to one asset class, e.g. /productDetails/...).
const RESOURCE_SEGMENTS = new Set(
  routes.filter((route) => route.meta && route.meta.assetClassScope === 'list').map((route) => route.path.split('/')[1])
)

// Whether the (route-relative or prefixed) pathname is a resource route.
export function isResourcePath(pathname) {
  return RESOURCE_SEGMENTS.has(stripSlugFromPath(pathname).split('/')[1])
}

function firstOf(available, candidates) {
  return candidates.find((slug) => slug && available.includes(slug)) || available[0] || null
}

// Slug to use before user/info is known (modules and the router are built from it). Never reads
// authorization, so it can differ from the final answer of resolveEntryAssetClass.
export function provisionalAssetClass(pathname, remembered) {
  const fromUrl = slugFromPath(pathname)
  if (fromUrl) {
    return fromUrl
  }
  if (isResourcePath(pathname)) {
    return LEGACY_ASSET_CLASS
  }
  return KNOWN_ASSET_CLASSES.includes(remembered) ? remembered : null
}

// Final answer once the authorized asset classes are known.
//   source 'url'          — the prefix names an authorized asset class;
//   source 'unauthorized' — the prefix names a known asset class the user cannot use (`rejected`);
//   source 'legacy'       — un-prefixed resource route: old links all meant IBIPs;
//   source 'entry'        — un-prefixed neutral route: last choice, then server default, then first.
export function resolveEntryAssetClass({ pathname, available, defaultAssetClass, remembered }) {
  if (!Array.isArray(available) || available.length === 0) {
    return { slug: null, source: 'entry' }
  }

  const fromUrl = slugFromPath(pathname)
  if (fromUrl && available.includes(fromUrl)) {
    return { slug: fromUrl, source: 'url' }
  }
  if (fromUrl) {
    return {
      slug: firstOf(available, [remembered, defaultAssetClass]),
      source: 'unauthorized',
      rejected: fromUrl,
    }
  }
  if (isResourcePath(pathname)) {
    return { slug: firstOf(available, [LEGACY_ASSET_CLASS, remembered, defaultAssetClass]), source: 'legacy' }
  }
  return { slug: firstOf(available, [remembered, defaultAssetClass]), source: 'entry' }
}

// What to do with the browser URL once the asset class of the page is settled.
//   none    — no asset class, or the URL already carries the right prefix;
//   rewrite — the router was built for this asset class (its base carries the prefix already), so the
//             URL is only rewritten in place, no reload;
//   reload  — the router was built for another asset class (a provisional slug that user/info overruled,
//             or a prefix the user cannot use): the page must reload on the right prefix.
export function planAssetClassUrl({ selected, routerAssetClass, pathname, search = '', hash = '' }) {
  if (!selected || slugFromPath(pathname) === selected) {
    return { action: 'none' }
  }

  const url = buildAssetClassUrl(selected, stripSlugFromPath(pathname)) + search + hash
  return { action: routerAssetClass === selected ? 'rewrite' : 'reload', url }
}
